import { Chunk, Context, Effect, Option, Stream, pipe } from "effect";
import { StripeApiBaseClient } from "./StripeApiBaseClient";
const make = Effect.fn(function*(use) {
    const getProductsStream = ()=>Stream.paginateChunkEffect(undefined, (cursor)=>use((client)=>client.products.list({
                    starting_after: cursor
                })).pipe(Effect.map((res)=>[
                    Chunk.fromIterable(res.data),
                    res.has_more ? Option.some(res.data[res.data.length - 1].id) : Option.none()
                ])));
    const getPricesForProduct = (productId)=>Stream.paginateChunkEffect(undefined, (cursor)=>use((client)=>client.prices.list({
                    product: productId,
                    starting_after: cursor
                })).pipe(Effect.map((res)=>[
                    Chunk.fromIterable(res.data),
                    res.has_more ? Option.some(res.data[res.data.length - 1].id) : Option.none()
                ])));
    const getSubscription = Effect.fn("Stripe.getSubscription")((subscriptionId)=>use((client)=>client.subscriptions.retrieve(subscriptionId, {
                expand: [
                    "schedule"
                ]
            })));
    const getInvoice = Effect.fn("Stripe.getInvoice")((invoiceId)=>use((client)=>client.invoices.retrieve(invoiceId)));
    const getInvoicesForSubscription = Effect.fn("Stripe.getInvoicesForSubscription")((subscriptionId)=>use((client)=>client.invoices.list({
                subscription: subscriptionId
            })));
    const getInvoiceWithSubscription = Effect.fn("Stripe.getInvoiceWithSubscription")((invoiceId)=>use((client)=>client.invoices.retrieve(invoiceId, {
                expand: [
                    "subscription"
                ]
            })));
    const getOriginalInvoiceForSubscription = Effect.fn("Stripe.getOriginalInvoiceForSubscription")((subscriptionId, subscriptionCreatedAt)=>Effect.gen(function*() {
            const subscriptionCreatedAt_ = subscriptionCreatedAt ? subscriptionCreatedAt : yield* getSubscription(subscriptionId).pipe(Effect.map((s)=>s.created));
            // Invoices will be created right before or after the subscription was
            // created. Unfortunately, we can't change the sort order on the list
            // so we just need to grab all the invoices created a couple days
            // before or after the subscription was created and then first the first
            // one.
            const invoices = yield* use((client)=>client.invoices.list({
                    subscription: subscriptionId,
                    limit: 100,
                    created: {
                        gte: subscriptionCreatedAt_ - 2 * 24 * 60 * 60,
                        lte: subscriptionCreatedAt_ + 2 * 24 * 60 * 60
                    }
                }));
            const invoice = invoices.data[invoices.data.length - 1];
            // It's possible that we don't have any invoices and we
            // could add a retry but I'm just going to return null
            // for now.
            return invoice;
        }));
    const getPaymentIntent = Effect.fn("Stripe.getPaymentIntent")((paymentIntentId)=>use((client)=>client.paymentIntents.retrieve(paymentIntentId)));
    const getPaymentIntentWithCharge = Effect.fn("getPaymentIntentWithCharge")((paymentIntentId)=>use((client)=>client.paymentIntents.retrieve(paymentIntentId, {
                expand: [
                    "latest_charge"
                ]
            })));
    const getCustomer = Effect.fn("Stripe.getCustomer")((customerId)=>use((client)=>client.customers.retrieve(customerId, {
                expand: [
                    "tax"
                ]
            })));
    const getTaxRate = Effect.fn("Stripe.getTaxRate")((taxRateId)=>use((client)=>client.taxRates.retrieve(taxRateId)));
    const getAccount = Effect.fn("Stripe.getTaxRate")(()=>use((client)=>client.accounts.retrieve()));
    return {
        use,
        getProductsStream,
        getPricesForProduct,
        getSubscription,
        getInvoice,
        getInvoicesForSubscription,
        getOriginalInvoiceForSubscription,
        getInvoiceWithSubscription,
        getPaymentIntent,
        getPaymentIntentWithCharge,
        getCustomer,
        getTaxRate,
        getAccount
    };
});
export class StripeApiClient extends Context.Tag("@open-revenue/stripe/StripeApiClient")() {
    static make = make;
    static makeWith = (instance)=>pipe(StripeApiBaseClient.make(instance), Effect.flatMap((base)=>make(base)));
}
